import { AxiosRequestConfig } from "axios";
import { BaseDataModel } from "@domain/CRM/BaseDataModel";
import { getImpersonatedEmail } from "../storage/impersonationStore";
import { showTestJobsStore } from "../storage/showTestJobsStore";
import { saveIdHeader } from "./saveId";
import { apiClient } from "./client";
import { inflateText } from "./inflate";
import { browserTokenStore } from "../storage/browserTokenStore";

import { env } from "../config/env";
export const USER_NOT_FOUND = "User not found";
export const USER_NOT_LINKED = "User must be linked to at least one company";
export const NEW_USER_TOKEN = "new_user";

export const REQUEST_CONFIG_NOAUTH: AxiosRequestConfig<any> = {
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
};

export function getLoginToken() {
	return browserTokenStore.getIdToken();
}
export function setLoginToken(token: string) {
	browserTokenStore.setIdToken(token);
}

interface AxiosConfigOptions {
	isFileUpload?: boolean;
	isCompressed?: boolean;
}

/** A shared request config, minus `paramsSerializer`. As of axios 1.20 that field's type is
 *  derived from the request's `params` shape (`ParamsSerializerOptions<unknown extends P ? ... : P>`),
 *  so a config declaring it as the `P = any` form no longer spreads into a call that adds a typed
 *  `params` literal — the serializer's parameter is contravariant, and tsc rejects the mismatch.
 *  Nothing here sets `paramsSerializer`, so omitting it keeps the helper spreadable at every call
 *  site regardless of that site's `params` type. */
type SharedAxiosConfig = Omit<AxiosRequestConfig<any>, "paramsSerializer">;

/**
 * @param seconds - The timeout duration in seconds for the request. Defaults to 0 (no timeout).
 */
export function StandardAxiosConfig(
	seconds: number = 0,
	{ isFileUpload, isCompressed }: AxiosConfigOptions = {},
): SharedAxiosConfig {
	const headers: Record<string, string> = { Authorization: `Bearer ${getLoginToken()}` };
	const impersonatedEmail = getImpersonatedEmail();
	if (impersonatedEmail) {
		headers["X-Impersonate-As"] = impersonatedEmail;
	}
	if (showTestJobsStore.get()) {
		headers["X-Show-Test-Jobs"] = "true";
	}
	Object.assign(headers, saveIdHeader());
	const config: SharedAxiosConfig = {
		headers,
		timeout: seconds * 1000,
	};
	if (isFileUpload) {
		(config.headers as any)["Content-Type"] = "multipart/form-data";
	}
	if (isCompressed) {
		config.responseType = "blob";
	}
	return config;
}

/** Recovers a JSON error body's string `detail` from an axios error, even when the request used
 *  `responseType: "blob"` — axios doesn't parse the body in that case, so a JSON error still
 *  arrives as raw bytes rather than a parsed object. Returns undefined when there's no string
 *  detail to show: a non-string `detail` (FastAPI's validation 422s carry a list), no response
 *  body at all, or a body that is not JSON (the HTML a gateway answers a 502/504 with). */
export async function blobErrorDetail(err: any): Promise<string | undefined> {
	let body = err?.response?.data;
	if (body instanceof Blob) {
		try {
			body = JSON.parse(await body.text());
		} catch {
			return undefined;
		}
	}
	const detail = body?.detail;
	return typeof detail === "string" ? detail : undefined;
}

export async function getCompressed(url: string, filter: Filter<any> = { fields: [] } as Filter<any>): Promise<any[]> {
	const response = await apiClient.post(url, filter, StandardAxiosConfig(0, { isCompressed: true }));
	const decompressedData = await inflateText(await response.data.arrayBuffer());
	const records = JSON.parse(decompressedData);
	return records;
}

// Filter shapes are defined in @ports/api/common so abstract ports
// and implementations can share them without crossing layer boundaries.
import type { Filter } from "@ports/api/common";
export type { Filter } from "@ports/api/common";

// Short duration JWT token (5-10 min)
export function setJwtToken(token: string) {
	browserTokenStore.setJwt(token);
}

// Longer duration refresh token (30-60 min)
export function setRefreshToken(token: string) {
	browserTokenStore.setRefreshToken(token);
}

export function getBaseDataURL(router: string) {
	return `${env.rogoApiUrl}/${router}`;
}

export function parseDates<U extends BaseDataModel>(records: U[]) {
	records.forEach((record) => {
		for (const key in record) {
			if (typeof record[key] !== "string" || !(key as string).toLowerCase().includes("date")) {
				continue;
			}
			const dateStr = record[key] as string;
			if (!dateStr || isNaN(Date.parse(dateStr))) {
				// Empty ("") or unparseable date strings must not stay strings — Date-typed
				// consumers (e.g. MRT cells calling .toLocaleDateString()) would crash. The
				// API returns "" for unset dates; normalize to undefined.
				record[key] = undefined as any;
			} else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
				// Date-only strings (YYYY-MM-DD) are parsed as UTC by JavaScript,
				// causing timezone offset issues. Append time to parse as local time.
				record[key] = new Date(dateStr + "T00:00:00") as any;
			} else {
				record[key] = new Date(dateStr) as any;
			}
		}
	});
}
